import { z } from 'zod';
import { STORAGE_PREFIX } from './storage.js';
/** Storage operations the key holder performs or signs (doc 74). Only below `couch/`. */
export const STORAGE_PART_BYTES = 64 * 1024 * 1024; // uniform parts: R2 wants every part but the last the same size
export const STORAGE_MAX_PARTS = 10_000;
export const STORAGE_PARTS_PER_CALL = 1_000;
export const STORAGE_GET_SECONDS = 60 * 60;
export const STORAGE_PUT_SECONDS = 12 * 60 * 60; // a big upload from a slow home line still starts its last part in time
/** How many 64 MiB parts a file of this size needs (at least one). */
export const storagePartCount = (bytes) => Math.max(1, Math.ceil(bytes / STORAGE_PART_BYTES));
/** UTF-8 length without TextEncoder (this package has no DOM or Node types); -1 for a control character or `\`. */
function utf8Length(s) {
    let n = 0;
    for (const ch of s) {
        const c = ch.codePointAt(0) ?? 0;
        if (c < 0x20 || c === 0x7f || ch === '\\')
            return -1;
        n += c < 0x80 ? 1 : c < 0x800 ? 2 : c < 0x10000 ? 3 : 4;
    }
    return n;
}
/**
 * An object key Couch may touch: below `couch/`, at most 1024 bytes, no empty, `.` or `..` segments, no control
 * characters or backslashes.
 */
export function isStorageObjectKey(key) {
    const length = utf8Length(key);
    if (!key.startsWith(STORAGE_PREFIX) || length < 0 || length > 1024)
        return false;
    return key.slice(STORAGE_PREFIX.length).split('/').every((s) => s !== '' && s !== '.' && s !== '..');
}
/** A prefix to list: `couch/` itself, or a key-like prefix ending in `/`. */
export const isStoragePrefix = (prefix) => prefix === STORAGE_PREFIX || (prefix.endsWith('/') && isStorageObjectKey(prefix.slice(0, -1)));
const ObjectKey = z.string().refine(isStorageObjectKey, 'Couch only uses its own folder couch/ in the bucket');
const UploadId = z.string().min(1).max(1024).regex(/^[\x21-\x7e]+$/);
const PartNumber = z.number().int().min(1).max(STORAGE_MAX_PARTS);
export const StorageOpSchema = z.discriminatedUnion('op', [
    z.object({ op: z.literal('presign_get'), key: ObjectKey, filename: z.string().max(255).optional() }),
    z.object({ op: z.literal('presign_put'), key: ObjectKey }),
    z.object({ op: z.literal('multipart_start'), key: ObjectKey }),
    z.object({ op: z.literal('presign_parts'), key: ObjectKey, uploadId: UploadId, parts: z.array(PartNumber).min(1).max(STORAGE_PARTS_PER_CALL) }),
    z.object({
        op: z.literal('multipart_complete'), key: ObjectKey, uploadId: UploadId,
        parts: z.array(z.object({ partNumber: PartNumber, etag: z.string().min(1).max(128).regex(/^[\x20-\x7e]+$/) })).min(1).max(STORAGE_MAX_PARTS),
    }),
    z.object({ op: z.literal('multipart_abort'), key: ObjectKey, uploadId: UploadId }),
    z.object({ op: z.literal('list'), prefix: z.string().refine(isStoragePrefix, 'Couch only lists its own folder couch/'), after: z.string().max(1024).optional() }),
    z.object({ op: z.literal('delete'), key: ObjectKey }),
]);
export const StorageObjectSchema = z.object({ key: z.string(), size: z.number().int().nonnegative(), modified: z.string() });
/** One answer shape for all operations; each fills what it has. */
export const StorageAnswerSchema = z.object({
    url: z.url().optional(),
    urls: z.array(z.object({ partNumber: PartNumber, url: z.url() })).optional(),
    uploadId: UploadId.optional(),
    objects: z.array(StorageObjectSchema).optional(),
    /** list: where the next page starts; null at the end. */
    next: z.string().nullable().optional(),
});
//# sourceMappingURL=storage-ops.js.map